import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

export default function registerCalendar(Alpine) {
  Alpine.data("calendar", (options = {}) => ({
    calendarInstance: null,
    darkObserver: null,
    currentViewType: null,

    init() {
      const el = this.$refs.calendar;
      if (!el) return;

      const { eventsUrl, initialView, viewPrefUrl } = options;
      this.currentViewType = initialView || "timeGridWeek";

      this.calendarInstance = new Calendar(el, {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: this.currentViewType,
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay"
        },
        events: {
          url: eventsUrl,
          failure: () => {
            console.error("Failed to load calendar events");
          }
        },
        nowIndicator: true,
        slotMinTime: "06:00:00",
        slotMaxTime: "20:00:00",
        allDaySlot: true,
        weekends: true,
        editable: false,
        selectable: false,
        height: "auto",
        locale: document.documentElement.lang || "en",
        firstDay: 1,
        dayMaxEvents: 3,
        eventTimeFormat: {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false
        },
        slotLabelFormat: {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false
        },
        eventClick: (info) => {
          info.jsEvent.preventDefault();
          const event = info.event;
          const props = event.extendedProps || {};

          const startDate = event.start;
          const endDate = event.end;
          const timeStr = event.allDay
            ? "All day"
            : `${this.formatTime(startDate)}${endDate ? ` – ${this.formatTime(endDate)}` : ""}`;

          window.dispatchEvent(
            new CustomEvent("calendar-event-click", {
              detail: {
                id: event.id,
                title: event.title,
                time: timeStr,
                location: props.location,
                description: props.description,
                attendees: props.attendees || [],
                url: event.url || props.html_link,
                crm_event: props.crm_event,
                crm_link: props.crm_link,
                status: props.status,
                type: props.type,
                rsvp_status: props.rsvp_status,
                rsvp_create_url: props.rsvp_create_url,
                rsvp_delete_url: props.rsvp_delete_url,
                meeting_url: props.meeting_url,
                training_session_id: props.training_session_id,
                accepted_count: props.accepted_count,
                max_participants: props.max_participants,
                provider_event_id: props.provider_event_id,
                conversion_url: props.conversion_url
              }
            })
          );
        },
        eventDidMount: (info) => {
          if (info.event.extendedProps?.crm_event) {
            info.el.classList.add("fc-event-crm");
          }
          if (info.event.extendedProps?.type === "training_session") {
            info.el.classList.add("fc-event-training");
            const rsvpStatus = info.event.extendedProps?.rsvp_status;
            if (rsvpStatus === "accepted") info.el.classList.add("fc-event-training-accepted");
            else if (rsvpStatus === "declined") info.el.classList.add("fc-event-training-declined");
          }

          // Tooltip with event title on hover
          if (info.event.title) {
            info.el.setAttribute("title", info.event.title);
          }
        },
        datesSet: (info) => {
          const newView = info.view.type;
          if (newView !== this.currentViewType) {
            this.currentViewType = newView;
            if (viewPrefUrl) this.persistViewPreference(newView, viewPrefUrl);
          }
        }
      });

      this.calendarInstance.render();

      // Re-render on dark mode toggle to pick up CSS variable changes
      this.darkObserver = new MutationObserver(() => {
        if (this.calendarInstance) {
          this.calendarInstance.render();
        }
      });
      this.darkObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"]
      });
    },

    formatTime(date) {
      if (!date) return "";
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },

    persistViewPreference(view, url) {
      const token = document.querySelector("meta[name='csrf-token']")?.content;
      fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": token || ""
        },
        credentials: "same-origin",
        body: JSON.stringify({ view })
      }).catch(() => {
        // Silent failure — the view still works, just not persisted
      });
    },

    destroy() {
      if (this.darkObserver) {
        this.darkObserver.disconnect();
        this.darkObserver = null;
      }
      if (this.calendarInstance) {
        this.calendarInstance.destroy();
        this.calendarInstance = null;
      }
    }
  }));
}
